import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useReportStockDiscrepancy } from "@/hooks/notifications/useReportStockDiscrepancy";
import { DiscrepancyReport } from "@/hooks/notifications/useDiscrepancyCount";
import { AlertTriangle } from "lucide-react";
import { z } from "zod";

const discrepancySchema = z.object({
  reportedAmount: z.number().nonnegative("Aantal moet 0 of meer zijn"),
  notes: z.string().max(500, "Opmerking mag maximaal 500 karakters bevatten").optional(),
});

interface StockDiscrepancyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notificationId: string;
  monitoringId?: string;
  productName: string;
  productSku?: string;
  productBc?: string;
  currentStock?: number;
  reportedBy?: string;
  reportedByName?: string;
  previousReports?: DiscrepancyReport[];
}

export const StockDiscrepancyDialog = ({
  open,
  onOpenChange,
  notificationId,
  monitoringId,
  productName,
  productSku,
  productBc,
  currentStock,
  reportedBy,
  reportedByName,
  previousReports = [],
}: StockDiscrepancyDialogProps) => {
  const [reportedAmount, setReportedAmount] = useState("0");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<{ reportedAmount?: string; notes?: string }>({});
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { mutate: reportDiscrepancy, isPending } = useReportStockDiscrepancy();

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    
    const amount = parseFloat(reportedAmount);
    
    // Validate
    try {
      discrepancySchema.parse({
        reportedAmount: amount,
        notes: notes || undefined,
      });
      setErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: { reportedAmount?: string; notes?: string } = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof typeof fieldErrors;
          fieldErrors[field] = err.message;
        });
        setErrors(fieldErrors);
        return;
      }
    }

    reportDiscrepancy(
      {
        notificationId,
        monitoringId,
        productName,
        productSku,
        productBc,
        reportedAmount: amount,
        expectedAmount: currentStock,
        notes: notes || undefined,
        reportedBy,
        reportedByName,
      },
      {
        onSuccess: () => {
          setReportedAmount("");
          setNotes("");
          setErrors({});
          onOpenChange(false);
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && reportedAmount && !notes) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Melding Voorraadverschil
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-3 border-b pb-4">
            <div className="space-y-1 mb-3">
              {(!previousReports || previousReports.length === 0) ? (
                <div className="text-sm text-muted-foreground italic">
                  Nog geen melding gemaakt
                </div>
              ) : (
                <>
                  {previousReports.map((report, index) => (
                    <div key={report.id} className="text-sm">
                      <span className="font-medium">{index + 1}e melding</span>
                      <span className="text-muted-foreground"> {report.reported_by_name || 'Onbekend'}</span>
                    </div>
                  ))}
                  <div className="text-sm font-semibold text-primary">
                    <span>{previousReports.length + 1}e melding</span>
                    <span> {reportedByName || 'Onbekend'}</span>
                  </div>
                </>
              )}
            </div>
            <p className="text-lg font-medium">{productName}</p>
            {currentStock !== undefined && (
              <p className="text-muted-foreground">
                Huidige voorraad: <span className="font-semibold">{currentStock}</span> stuks
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="reportedAmount" className="text-base">
              Aantal gevonden <span className="text-destructive">*</span>
            </Label>
            <Input
              ref={inputRef}
              id="reportedAmount"
              type="number"
              step="0.01"
              min="0"
              value={reportedAmount}
              onChange={(e) => setReportedAmount(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Voer aantal in"
              className="text-xl p-4 h-14 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
              required
            />
            {errors.reportedAmount && (
              <p className="text-sm text-destructive">{errors.reportedAmount}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-base">
              Opmerking (optioneel)
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Eventuele toelichting..."
              className="text-base p-4 min-h-[100px] resize-none"
              maxLength={500}
            />
            {errors.notes && (
              <p className="text-sm text-destructive">{errors.notes}</p>
            )}
            <p className="text-xs text-muted-foreground text-right">
              {notes.length}/500
            </p>
          </div>

          <DialogFooter className="flex-row gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
              className="flex-1"
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              disabled={isPending || !reportedAmount}
              className="flex-1"
            >
              {isPending ? "Bezig..." : "Opslaan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
